import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Camera, Download, Factory, Package, PackagePlus, ScanBarcode, Ship, ShieldAlert, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { BarcodeField } from '@/components/work/BarcodeField';
import { UnitSheet } from '@/components/traceability/UnitSheet';
import { ShipUnitsDialog } from '@/components/traceability/ShipUnitsDialog';
import { RegisterUnitDialog } from '@/components/traceability/RegisterUnitDialog';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDate, formatDateTime } from '@/lib/formatters';
import { formatDuration } from '@/features/workMode/api';
import { useWorkLookups } from '@/features/workMode/useLookups';
import { useWorkRealtime } from '@/features/workMode/hooks';
import { findUnitBySerial, useSerializedUnits } from '@/features/traceability/hooks';
import { personLabel, UNIT_STATUS_LABEL, UNIT_STATUS_VARIANT, unitDestination, type SerializedUnitRow } from '@/features/traceability/types';
import { downloadCsv } from '@/features/manufacturing/bomExport';

function facetOptions(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

function photoCount(u: SerializedUnitRow): number {
  return (u.photo_paths?.length ?? 0) + (u.label_photo_path ? 1 : 0);
}

function lotsSummary(u: SerializedUnitRow): string {
  return (u.component_lots ?? []).map((l) => `${l.sku || l.name}: ${l.lot_number}`).join('; ');
}

/**
 * Manufacturing → Production → Unit log. Every serialized unit ever built:
 * when, by whom, QC, photos, the component lots inside it, and where it was
 * sold. Scan a barcode at the top to jump straight to a unit.
 */
export function UnitLogPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const lookups = useWorkLookups();
  const [params, setParams] = useSearchParams();
  const { data, isLoading } = useSerializedUnits(null);
  const rows = useMemo<SerializedUnitRow[]>(() => data ?? [], [data]);

  useWorkRealtime(true);

  const [selectedId, setSelectedId] = useState<string | null>(params.get('unit'));
  const [shipOpen, setShipOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [lookup, setLookup] = useState('');
  const [looking, setLooking] = useState(false);

  const openUnit = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      const p = new URLSearchParams(params);
      if (id) p.set('unit', id);
      else p.delete('unit');
      p.delete('serial');
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  const findSerial = useCallback(
    async (serial: string) => {
      const s = serial.trim();
      if (!s) return;
      setLooking(true);
      try {
        const local = rows.find((r) => r.serial_number.toUpperCase() === s.toUpperCase());
        const hit = local ?? (await findUnitBySerial(s));
        if (hit) {
          setLookup('');
          openUnit(hit.id);
        } else {
          toast.error(`${s} is not in the unit log.`);
        }
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLooking(false);
      }
    },
    [rows, openUnit],
  );

  // Deep link: /mfg/production/units?serial=SN-000123
  const serialParam = params.get('serial');
  useEffect(() => {
    if (serialParam && !isLoading) void findSerial(serialParam);
    // Only when the param or data readiness changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialParam, isLoading]);

  const stats = useMemo(() => {
    const cutoff = Date.now() - 30 * 86_400_000;
    let built30 = 0;
    let inStock = 0;
    let onHold = 0;
    let shipped = 0;
    let noPhotos = 0;
    for (const r of rows) {
      if (Date.parse(r.built_at) >= cutoff) built30 += 1;
      if (r.status === 'built') inStock += 1;
      if (r.status === 'on_hold') onHold += 1;
      if (r.status === 'shipped') shipped += 1;
      if (photoCount(r) === 0) noPhotos += 1;
    }
    return { built30, inStock, onHold, shipped, noPhotos };
  }, [rows]);

  const columns = useMemo<ColumnDef<SerializedUnitRow>[]>(
    () => [
      {
        id: 'serial',
        accessorKey: 'serial_number',
        header: 'Serial',
        size: 160,
        minSize: 120,
        cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs font-medium text-foreground">{row.original.serial_number}</span>,
      },
      {
        id: 'status',
        accessorFn: (r) => UNIT_STATUS_LABEL[r.status],
        header: 'Status',
        size: 110,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <span className="flex items-center gap-1">
            <Badge variant={UNIT_STATUS_VARIANT[row.original.status]}>{UNIT_STATUS_LABEL[row.original.status]}</Badge>
            {row.original.qc_result === 'fail' && <ShieldAlert className="h-3.5 w-3.5 text-fresh-salmon-dark" aria-label="QC fail" />}
          </span>
        ),
      },
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Link to={`/catalog/items/${row.original.item_id}`} onClick={(e) => e.stopPropagation()} className="whitespace-nowrap font-mono text-xs text-foreground hover:underline">
            {row.original.items?.sku ?? '—'}
          </Link>
        ),
      },
      {
        id: 'item',
        accessorFn: (r) => r.items?.name ?? '',
        header: 'Product',
        size: 220,
        minSize: 140,
        cell: ({ row }) => <span className="font-medium">{row.original.items?.name ?? '—'}</span>,
      },
      {
        id: 'built_at',
        accessorKey: 'built_at',
        header: 'Built',
        size: 150,
        minSize: 110,
        cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(row.original.built_at)}</span>,
      },
      {
        id: 'builder',
        accessorFn: (r) => personLabel(r.builder),
        header: 'Built by',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
      },
      {
        id: 'station',
        accessorFn: (r) => r.workstations?.name ?? '',
        header: 'Station',
        size: 130,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => row.original.workstations?.name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'build_time',
        accessorFn: (r) => r.build_seconds ?? 0,
        header: () => <span className="block text-right">Build time</span>,
        size: 100,
        minSize: 80,
        sortingFn: 'basic',
        cell: ({ row }) => <span className="block text-right tabular-nums text-muted-foreground">{row.original.build_seconds != null ? formatDuration(row.original.build_seconds) : '—'}</span>,
      },
      {
        id: 'qc',
        accessorFn: (r) => (r.qc_result ? `${r.qc_result === 'pass' ? 'Pass' : 'Fail'} · ${personLabel(r.inspector)}` : ''),
        header: 'QC',
        size: 170,
        minSize: 110,
        cell: ({ row }) => {
          const u = row.original;
          if (!u.qc_result) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Badge variant={u.qc_result === 'pass' ? 'success-soft' : 'warning-soft'}>{u.qc_result === 'pass' ? 'Pass' : 'Fail'}</Badge>
              <span className="truncate text-xs text-muted-foreground">{personLabel(u.inspector)}</span>
            </span>
          );
        },
      },
      {
        id: 'photos',
        accessorFn: (r) => photoCount(r),
        header: () => <span className="block text-right">Photos</span>,
        size: 80,
        minSize: 60,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const n = photoCount(row.original);
          return (
            <span className={n === 0 ? 'flex items-center justify-end gap-1 text-fresh-salmon-dark' : 'flex items-center justify-end gap-1 tabular-nums text-muted-foreground'}>
              <Camera className="h-3.5 w-3.5" />
              {n}
            </span>
          );
        },
      },
      {
        id: 'lots',
        accessorFn: (r) => lotsSummary(r),
        header: 'Component lots',
        size: 220,
        minSize: 120,
        cell: ({ row }) => {
          const lots = row.original.component_lots ?? [];
          if (lots.length === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="block truncate font-mono text-[11px] text-muted-foreground" title={lotsSummary(row.original)}>
              {lots.length} lot{lots.length === 1 ? '' : 's'} · {lots.map((l) => l.lot_number).join(', ')}
            </span>
          );
        },
      },
      {
        id: 'customer',
        accessorFn: (r) => r.customers?.name ?? '',
        header: 'Sold to',
        size: 160,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => row.original.customers?.name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'destination',
        accessorFn: (r) => unitDestination(r) ?? '',
        header: 'Where it went',
        size: 220,
        minSize: 120,
        cell: ({ row }) => {
          const d = unitDestination(row.original);
          return d ? <span className="block truncate text-muted-foreground" title={d}>{d}</span> : <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: 'shipped_at',
        accessorFn: (r) => r.shipped_at ?? '',
        header: 'Shipped',
        size: 110,
        minSize: 90,
        cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{row.original.shipped_at ? formatDate(row.original.shipped_at) : '—'}</span>,
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'status', title: 'Status', options: facetOptions(rows.map((r) => UNIT_STATUS_LABEL[r.status])) },
      { columnId: 'sku', title: 'SKU', options: facetOptions(rows.map((r) => r.items?.sku)) },
      { columnId: 'builder', title: 'Built by', options: facetOptions(rows.map((r) => personLabel(r.builder))) },
      { columnId: 'station', title: 'Station', options: facetOptions(rows.map((r) => r.workstations?.name)) },
      { columnId: 'customer', title: 'Sold to', options: facetOptions(rows.map((r) => r.customers?.name)) },
    ],
    [rows],
  );

  const exportCsv = () => {
    const header = [
      'Serial',
      'Status',
      'SKU',
      'Product',
      'Built at',
      'Built by',
      'Station',
      'Build time (s)',
      'QC result',
      'QC by',
      'QC at',
      'QC notes',
      'Photos',
      'Component lots',
      'Customer',
      'Destination',
      'Sales order',
      'Shipment',
      'Shipped at',
      'Shipped by',
      'Notes',
    ];
    const body = rows.map((r) => [
      r.serial_number,
      UNIT_STATUS_LABEL[r.status],
      r.items?.sku ?? '',
      r.items?.name ?? '',
      r.built_at,
      personLabel(r.builder),
      r.workstations?.name ?? '',
      r.build_seconds ?? '',
      r.qc_result ?? '',
      r.qc_result ? personLabel(r.inspector) : '',
      r.qc_at ?? '',
      r.qc_notes ?? '',
      photoCount(r),
      lotsSummary(r),
      r.customers?.name ?? '',
      r.destination ?? '',
      r.sales_orders?.so_number ?? '',
      r.shipments?.shipment_number ?? '',
      r.shipped_at ?? '',
      r.shipped_by ? lookups.personName(r.shipped_by) : '',
      r.notes ?? '',
    ]);
    downloadCsv(`unit-log-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...body]);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing / Production"
        title="Unit log"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/mfg/production">
                <Factory className="h-4 w-4" />
                Production
              </Link>
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={rows.length === 0}>
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            {canManage && (
              <>
                <Button variant="outline" onClick={() => setRegisterOpen(true)}>
                  <PackagePlus className="h-4 w-4" />
                  Register unit
                </Button>
                <Button onClick={() => setShipOpen(true)}>
                  <Ship className="h-4 w-4" />
                  Ship units
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Built (30 days)" value={stats.built30.toLocaleString()} loading={isLoading} icon={<Package className="h-4 w-4" />} hint={`${rows.length.toLocaleString()} units all time`} />
        <MetricCard label="In stock" value={stats.inStock.toLocaleString()} loading={isLoading} hint="Built, passed, not shipped" tone="positive" />
        <MetricCard label="On hold" value={stats.onHold.toLocaleString()} loading={isLoading} icon={<ShieldAlert className="h-4 w-4" />} tone={stats.onHold > 0 ? 'negative' : 'neutral'} hint={stats.noPhotos ? `${stats.noPhotos} without photos` : undefined} />
        <MetricCard label="Shipped" value={stats.shipped.toLocaleString()} loading={isLoading} icon={<Truck className="h-4 w-4" />} tone="info" />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground sm:w-56">
            <ScanBarcode className="h-5 w-5" />
            <span>Scan a barcode to open its unit</span>
          </div>
          <BarcodeField value={lookup} onChange={setLookup} onScanned={(s) => void findSerial(s)} placeholder="Scan or type a serial number, then Enter" disabled={looking} className="flex-1" inputClassName="h-12" />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <DataTable<SerializedUnitRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => openUnit(r.id)}
            searchPlaceholder="Search by serial, SKU, product, lot, builder, customer…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 250]}
            tableId="manufacturing-unit-log"
            enableColumnResizing
            mobileCard={{
              title: (r) => r.serial_number,
              subtitle: (r) => (
                <span>
                  {r.items?.name ?? '—'} · <span className="font-mono text-[11px]">{r.items?.sku}</span>
                </span>
              ),
              badges: (r) => [
                <Badge key="status" variant={UNIT_STATUS_VARIANT[r.status]}>
                  {UNIT_STATUS_LABEL[r.status]}
                </Badge>,
                r.qc_result === 'fail' ? (
                  <Badge key="qc" variant="warning-soft">
                    QC fail
                  </Badge>
                ) : null,
              ],
              meta: (r) => [
                { label: 'Built', value: `${formatDate(r.built_at)} · ${personLabel(r.builder)}` },
                { label: 'Where', value: unitDestination(r) ?? 'Not shipped' },
              ],
            }}
            emptyState={
              <EmptyState
                icon={Package}
                title="No serialized units yet"
                detail="Units are logged from Work Mode when a worker scans the serial barcode on a finished product."
                action={
                  canManage ? (
                    <Button variant="outline" onClick={() => setRegisterOpen(true)}>
                      <PackagePlus className="h-4 w-4" />
                      Register a unit
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      <UnitSheet unitId={selectedId} lookups={lookups} canManage={canManage} onOpenChange={(o) => !o && openUnit(null)} />
      {canManage && <ShipUnitsDialog open={shipOpen} onOpenChange={setShipOpen} />}
      {canManage && <RegisterUnitDialog open={registerOpen} onOpenChange={setRegisterOpen} lookups={lookups} onRegistered={(id) => openUnit(id)} />}
    </div>
  );
}
